import React from "react";
import { QRCodeSVG } from "qrcode.react";

/**
 * BookingQR — Reusable booking-QR renderer.
 *
 * • Renders the qr_token as a scannable QR (SVG) using qrcode.react.
 * • Always shows the raw token text below, so it stays copyable and is
 *   compatible with offline / non-scanner door validation.
 * • Pure presentational — does NOT alter the token, the validation
 *   logic, or any state. Same component used on the checkout success
 *   screen and (when ready) inside the transactional email template.
 *
 * Props:
 *   value (string)        : the qr_token to encode (required)
 *   size  (number=180)    : pixel size of the SVG
 *   label (string|null)   : optional caption rendered above the QR
 *   helperText (string|null) : optional caption below the token
 *   className (string)    : extra wrapper classes
 */
export const BookingQR = ({
  value,
  size = 180,
  label = "Tu código QR",
  helperText = "Muéstralo en recepción al llegar al centro.",
  className = "",
  testId = "booking-qr",
}) => {
  if (!value) return null;
  return (
    <div
      data-testid={testId}
      className={`bg-white border border-sage/40 rounded-2xl p-6 space-y-4 ${className}`}
    >
      {label && (
        <div className="flex items-center justify-center gap-2 text-sage-deep">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold">{label}</span>
        </div>
      )}
      <div className="flex justify-center">
        <div className="bg-white p-3 rounded-xl border border-forest/10">
          <QRCodeSVG
            value={value}
            size={size}
            level="M"
            includeMargin={false}
            fgColor="#2c3e2d"
            bgColor="#ffffff"
            data-testid={`${testId}-svg`}
          />
        </div>
      </div>
      <p
        data-testid={`${testId}-token`}
        className="font-mono text-sm break-all text-stone bg-mist p-3 rounded-md text-center"
      >
        {value}
      </p>
      {helperText && (
        <p className="text-[11px] text-ash text-center">{helperText}</p>
      )}
    </div>
  );
};

export default BookingQR;
